import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { User, AuthContextValue } from '../types';
import { setCookie, getCookie, deleteCookie } from '../utils/cookies';

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const INACTIVITY_TIMEOUT = 10 * 60 * 1000; // 10 minutes in milliseconds
const COOKIE_MAX_AGE = 10 * 60; // 10 minutes in seconds

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const inactivityTimerRef = useRef<number | null>(null);

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Only set timer if user is logged in
    const token = getCookie('authToken');
    if (token) {
      // Refresh cookie expiration
      setCookie('authToken', token, { maxAge: COOKIE_MAX_AGE });
      const userData = getCookie('user');
      if (userData) {
        setCookie('user', userData, { maxAge: COOKIE_MAX_AGE });
      }

      // Set new inactivity timer
      inactivityTimerRef.current = window.setTimeout(() => {
        logout();
      }, INACTIVITY_TIMEOUT);
    }
  }, []);

  // Track user activity
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      resetInactivityTimer();
    };

    // Add event listeners for user activity
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Initial timer setup
    resetInactivityTimer();

    return () => {
      // Cleanup
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [resetInactivityTimer]);

  useEffect(() => {
    // Check for stored token on mount
    const storedToken = getCookie('authToken');
    const storedUser = getCookie('user');
    
    if (storedToken && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        resetInactivityTimer();
      } catch (error) {
        console.error('Error parsing stored user:', error);
        deleteCookie('authToken');
        deleteCookie('user');
      }
    }
    
    setIsLoading(false);
  }, [resetInactivityTimer]);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }

      const data = await response.json();
      
      // Decode the JWT token to get the user's Cognito sub (user ID)
      const tokenPayload = JSON.parse(atob(data.accessToken.split('.')[1]));
      const userId = tokenPayload.sub;
      
      const user: User = {
        id: userId, // Use Cognito sub as the user ID
        email,
        token: data.accessToken,
      };

      setUser(user);
      
      // Store in cookies with 10-minute expiration
      setCookie('authToken', data.accessToken, { maxAge: COOKIE_MAX_AGE });
      setCookie('user', JSON.stringify(user), { maxAge: COOKIE_MAX_AGE });
      
      // Reset inactivity timer
      resetInactivityTimer();
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (email: string, password: string) => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Registration failed');
      }

      // After successful registration, automatically log in
      await login(email, password);
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    deleteCookie('authToken');
    deleteCookie('user');
    deleteCookie('lastPath');
    
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  const value: AuthContextValue = {
    user,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  // Don't render children until we've checked for existing auth
  if (isLoading) {
    return null;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
